import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo
} from 'react';

import { AnimatePresence, useReducedMotion } from 'framer-motion';
import PropTypes from 'prop-types';

import AnimatedGroup, { getChildVariant, ANIMATION_STYLE } from './AnimatedGroup';
import * as Styled from './DynamicRenderer.style';
import InstructionGroup from './InstructionGroup';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useSmartScroll } from '../../hooks/useSmartScroll';
import { ComponentTypes } from '../utils/constants';

/**
 * Find the latest instruction group index containing specific UserResponse type
 * @param {Array} allInstructions - All instruction groups
 * @param {Function} predicate - Function to test each instruction
 * @returns {number} Latest index or -1 if none found
 */
function findLatestUserResponseIndex(allInstructions, predicate) {
  let latestIndex = -1;
  allInstructions.forEach((inst, index) => {
    const hasMatchingResponse = inst.attributes?.uiInstructions?.some(predicate);
    if (hasMatchingResponse) {
      latestIndex = Math.max(latestIndex, index);
    }
  });
  return latestIndex;
}

/***************************************************************************
 * DynamicRenderer
 * ------------------------------------------------------------
 *  ‑ Staggers each instruction group in with a selectable motion preset.
 *  ‑ Scrolls to new content immediately without offsets or timeouts.
 *  ‑ Three scroll scenarios:
 *    1. GET mode (refresh/reload): scroll to bottom
 *    2. UserResponse added: scroll to bottom
 *    3. POST mode (new content): scroll new content into view
 ***************************************************************************/

/**
 * Main component that renders UI instructions
 *
 * A dynamic renderer component that renders UI instructions
 * @param {object} props - Component props
 * @param {Array} props.instructions - Array of UI instructions to render
 * @param {Function} props.onComponentClick - Callback function for component clicks
 * @param {string} props.currentMode - Current mode ('get' or 'post') for animation control
 * @param {Function} props.onIntersectionChange - Callback for intersection changes
 * @returns {React.ReactElement} The DynamicRenderer component
 */
const DynamicRenderer = forwardRef(function DynamicRenderer(
  {
    instructions,
    onComponentClick = () => {},
    currentMode,
    onIntersectionChange = () => {},
    fullWidth = false,
    isChatMode = true
  },
  ref
) {
  const groupRefs = useRef([]);

  // Two separate counters to avoid race conditions:
  const previousInstructionCount = useRef(0); // For scroll logic - updates immediately
  const previousAnimationCount = useRef(0); // For animation logic - updates on completion

  const containerRef = useRef(null);
  const bottomSentinelRef = useRef(null); // Fixed sentinel element
  const shouldAnimate = currentMode === 'post' || currentMode === 'initial';
  const baseChildVariant = getChildVariant(ANIMATION_STYLE);

  const prefersReducedMotion = useReducedMotion();

  // Add ref to track and prevent competing scroll operations
  const scrollOperationRef = useRef({
    isScrolling: false,
    lastScrollTime: 0,
    lastScrollReason: null
  });

  // Forward the containerRef to parent
  useImperativeHandle(ref, () => containerRef.current);

  // Use intersection observer to track bottom sentinel visibility
  const isIntersecting = useIntersectionObserver(bottomSentinelRef, {
    root: containerRef.current,
    rootMargin: '0px', // Negative bottom margin to account for input area
    threshold: 0.84 // Only hide arrow when 100% of last element is visible
  });

  // Notify parent of intersection changes
  useEffect(() => {
    if (onIntersectionChange) {
      onIntersectionChange(isIntersecting);
    }
  }, [isIntersecting, onIntersectionChange]);

  // Use the enhanced smart scroll hook with observers
  const { scrollToElement, scrollToBottom, scrollToBottomWhenReady, scrollToElementWhenReady } =
    useSmartScroll();

  // Smart scroll wrapper that prevents competing operations
  const performSmartScroll = useCallback(
    (scrollFunction, reason, targetElement = null) => {
      const now = Date.now();
      const timeSinceLastScroll = now - scrollOperationRef.current.lastScrollTime;
      const minScrollInterval = 150; // Minimum time between scroll operations

      // Prevent rapid competing scroll operations
      if (timeSinceLastScroll < minScrollInterval && scrollOperationRef.current.isScrolling) {
        return;
      }

      // Update scroll tracking
      scrollOperationRef.current = {
        isScrolling: true,
        lastScrollTime: now,
        lastScrollReason: reason
      };

      // Execute the appropriate scroll function
      if (reason === 'USER_RESPONSE_SCROLL') {
        // User just clicked a button - scroll normally to bottom
        scrollToBottomWhenReady(containerRef.current, {
          smooth: true,
          maxWaitTime: 200,
          respectViewportTop: false
        });
      } else if (reason === 'API_RESPONSE_SCROLL') {
        // API response coming in - use viewport-aware scrolling
        scrollToBottomWhenReady(containerRef.current, {
          smooth: true,
          maxWaitTime: 700,
          respectViewportTop: true
        });
      } else if (reason === 'NEW_CONTENT_SCROLL' && targetElement) {
        // Use observer-based scroll for new content - waits for element to be ready
        // Enable viewport-aware scrolling to prevent scrolling past browser top
        scrollToElementWhenReady(targetElement, {
          block: 'start',
          maxWaitTime: 600,
          respectViewportTop: true
        });
      } else if (targetElement) {
        // Fallback for other element scrolls
        scrollFunction(targetElement, { block: 'start' });
      } else {
        // Fallback for container scrolls
        scrollFunction(containerRef.current);
      }

      // Reset scrolling flag after a delay
      setTimeout(() => {
        scrollOperationRef.current.isScrolling = false;
      }, 800); // Longer timeout to account for observer delays
    },
    [scrollToBottomWhenReady, scrollToElementWhenReady]
  );

  // Create mode-aware child variants - we'll pass the appropriate variant per component
  const getChildVariantForComponent = (isNewComponent) => {
    if (prefersReducedMotion) {
      return {
        hidden: { opacity: 1 },
        visible: { opacity: 1, transition: { duration: 0 } }
      };
    }

    const variant = (() => {
      if (currentMode === 'get') {
        // For GET mode, return static variants (no animation)
        return {
          hidden: { opacity: 1 },
          visible: { opacity: 1, transition: { duration: 0 } }
        };
      }
      if (currentMode === 'initial') {
        return baseChildVariant; // Full animation for initial loads
      }
      if (currentMode === 'post' && isNewComponent) {
        return baseChildVariant; // Full animation for new components in POST mode
      }
      // For existing components in POST mode, return static scrollToBottomWhenReady (no animation)
      return {
        hidden: { opacity: 1 },
        visible: { opacity: 1, transition: { duration: 0 } }
      };
    })();

    return variant;
  };

  // Animation completion handler - updates animation counter
  const handleAnimationComplete = useCallback(() => {
    previousAnimationCount.current = instructions.length;
  }, [instructions.length]);

  // Initialize animation counter for GET mode (existing thread loading)
  // This prevents existing content from re-animating when new content is added
  useEffect(() => {
    if (currentMode === 'get' && instructions.length > 0) {
      // For GET mode, immediately mark all loaded instructions as "animated"
      // so they don't re-animate when we switch to POST mode
      previousAnimationCount.current = instructions.length;
    }
  }, [currentMode, instructions.length]);

  // Scenario 1: GET mode (refresh/reload) - scroll to bottom
  useEffect(() => {
    if (currentMode === 'get' && instructions.length > 0) {
      performSmartScroll(scrollToBottom, 'GET_MODE_SCROLL');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode, performSmartScroll, scrollToBottom]);

  // Scenario 2 & 3: Handle UserResponse and new content scrolling
  useEffect(() => {
    // Only run for POST mode to avoid conflicts with GET mode
    if (currentMode !== 'post') {
      return;
    }

    const hasNewInstructions = instructions.length > previousInstructionCount.current;
    const isUserResponseAddition =
      instructions.length === previousInstructionCount.current &&
      instructions.length > 0 &&
      currentMode === 'post';

    // Scenario 3: POST mode (new content) - scroll new content into view
    if (currentMode === 'post' && hasNewInstructions) {
      const newComponentIndex = instructions.length - 1; // Last instruction is the new one

      // Use a minimal delay to ensure React has processed the state change
      setTimeout(() => {
        const element = groupRefs.current[newComponentIndex - 1]; // Previous element contains content to scroll to

        if (element) {
          const userResponse = element.querySelector('[class*="UserResponse"]');
          const lastChild = element.querySelector('.DynamicRenderer-componentWrapper:last-child');
          const scrollTarget = userResponse || lastChild || element;

          // Use observer-based scroll with viewport awareness to prevent scrolling past browser top
          performSmartScroll(scrollToElement, 'NEW_CONTENT_SCROLL', scrollTarget);
        }
      }, 16); // Single frame delay to ensure React has updated
    }

    // Scenario 2: UserResponse added - scroll to bottom
    if (isUserResponseAddition) {
      // Debug: What's in the last instruction?
      const lastInstruction = instructions[instructions.length - 1];
      const lastInstructionComponents = lastInstruction?.attributes?.uiInstructions || [];

      // console.log('Last instruction components:', lastInstructionComponents.map(c => c.componentType));

      // Check if the LAST component in the last instruction is a UserResponse
      const lastComponent = lastInstructionComponents[lastInstructionComponents.length - 1];
      const isUserResponseLast = lastComponent?.componentType === 'UserResponse';

      // Check if any instruction has a UserResponse
      const hasUserResponse = instructions.some((instruction) =>
        instruction.attributes?.uiInstructions?.some((ui) => ui.componentType === 'UserResponse')
      );

      if (hasUserResponse) {
        // If last element is UserResponse, just scroll to bottom (user clicked button)
        // Otherwise, use viewport-aware scroll (API response)
        if (isUserResponseLast) {
          performSmartScroll(scrollToBottom, 'USER_RESPONSE_SCROLL');
        } else {
          performSmartScroll(scrollToBottom, 'API_RESPONSE_SCROLL');
        }
      }
    }

    // Update previous instruction count for next render (do this last)
    previousInstructionCount.current = instructions.length;
  }, [instructions, currentMode, performSmartScroll, scrollToElement, scrollToBottom]);

  // Memoize expensive UserResponse index calculations
  const { latestTextUserResponseIndex, latestUserResponseIndex } = useMemo(() => {
    const textResponseIndex = findLatestUserResponseIndex(
      instructions,
      (uiInst) =>
        uiInst.componentType === ComponentTypes.USER_RESPONSE &&
        uiInst.props?.messageType === 'text'
    );

    const anyResponseIndex = findLatestUserResponseIndex(
      instructions,
      (uiInst) => uiInst.componentType === ComponentTypes.USER_RESPONSE
    );

    return {
      latestTextUserResponseIndex: textResponseIndex,
      latestUserResponseIndex: anyResponseIndex
    };
  }, [instructions]);

  // Update instruction count immediately for scroll logic
  useLayoutEffect(() => {
    previousInstructionCount.current = instructions.length;
  }, [instructions]);

  if (!Array.isArray(instructions)) return null;

  // Early return for null/invalid instructions
  if (!instructions || !Array.isArray(instructions)) {
    return null;
  }

  return (
    <Styled.DynamicRendererRoot ref={containerRef} ischatmode={isChatMode ? 'true' : undefined}>
      <AnimatePresence>
        {instructions.map((instruction, index) => {
          // Fix: Ensure unique keys by combining ID with index
          const baseKey = instruction.renderKey || instruction.id || `instruction-${index}`;
          const key = `${baseKey}-group-${index}`;

          // In POST mode, only animate new components (those beyond the previous count)
          const isNewComponent = shouldAnimate && index >= previousAnimationCount.current;

          // Ref for AnimatedGroup (existing functionality)
          const setAnimatedGroupRef = (el) => {
            groupRefs.current[index] = el;
          };

          // Check if this is the last instruction group (the sentinel)
          return (
            <Styled.DynamicRendererInstructionGroup
              key={key}
              className={isNewComponent ? 'new-component-rendering' : ''}
              fullWidth={fullWidth}
            >
              <AnimatedGroup
                ref={setAnimatedGroupRef}
                animationStyle={ANIMATION_STYLE}
                currentMode={currentMode}
                fullWidth={fullWidth}
                index={index}
                isNewComponent={isNewComponent}
                onAnimationComplete={handleAnimationComplete}
              >
                <InstructionGroup
                  childVariant={getChildVariantForComponent(isNewComponent)}
                  currentIndex={index}
                  currentMode={currentMode}
                  instruction={instruction}
                  isNewComponent={isNewComponent}
                  latestTextUserResponseIndex={latestTextUserResponseIndex}
                  latestUserResponseIndex={latestUserResponseIndex}
                  nextInstruction={instructions[index + 1]}
                  onComponentClick={onComponentClick}
                />
              </AnimatedGroup>
            </Styled.DynamicRendererInstructionGroup>
          );
        })}
      </AnimatePresence>

      {/* Bottom sentinel that flows with content for intersection detection */}
      <Styled.DynamicRendererBottomSentinel ref={bottomSentinelRef} data-testid="bottom-sentinel" />
    </Styled.DynamicRendererRoot>
  );
});

DynamicRenderer.displayName = 'DynamicRenderer';

DynamicRenderer.propTypes = {
  instructions: PropTypes.array.isRequired,
  onComponentClick: PropTypes.func,
  currentMode: PropTypes.oneOf(['get', 'post', 'initial']).isRequired,
  onIntersectionChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  isChatMode: PropTypes.bool
};

export default DynamicRenderer;
